// The single source of truth for the two-slider estimate.
//
// /calculator and /quote both render the same estimator and must never show a
// different premium for the same inputs. Everything that decides the number —
// the slider bounds, the defaults the "from £x a month" claim rests on, the
// placeholder postcode, the optional-cover selection and the request shape
// itself — lives here, so the two pages cannot drift apart.
//
// The premium is still computed by the pricing engine, reached through
// /api/policy/v1/price (live slider moves) and /api/policy/v1/entry-price (the
// cached, server-rendered default). This module is the one place the frontend
// asks for it.

// Matches the quote form's own rule (min_bike_value:400, max_bike_value:15000)
// so the slider can't produce an estimate for a bike we won't insure.
export const BIKE_MIN = 400
export const BIKE_MAX = 15000
export const BIKE_STEP = 200

// 16 is the minimum the real date-of-birth field enforces. 99 matches the
// quote API's own maximum — the policy itself states no maximum age.
export const AGE_MIN = 16
export const AGE_MAX = 99

// The default slider position. Also the configuration the published "from
// £x a month" figure is computed for, so the two always agree.
export const DEFAULT_BIKE_VALUE = 400
export const DEFAULT_AGE = 29

// We don't have the customer's postcode at this point in the funnel, so both
// the estimate and the quote we hand to the modal use this one. Same value the
// homepage estimator and the server-side entry price already use.
export const PLACEHOLDER_POSTCODE = 'SA10 6AA'

// An estimate is the base policy only — every optional cover is priced later,
// inside the quote itself.
export const ESTIMATE_COMPONENTS = {
  accessories_cover: 'none',
  amateur_racing: 'none',
  loss_of_earnings: 'none',
  personal_injury: 'none',
  public_liability: 'none',
  worldwide_cover: 'none',
}

// Bike types a landing page may deep-link with. Anything outside this list is
// ignored rather than rendered, so ?bike= can never put attacker-controlled
// text on the page.
export const BIKE_TYPES = {
  road: 'Road bike',
  electric: 'Electric bike',
  mountain: 'Mountain bike',
  cargo: 'Cargo bike',
  commuter: 'Commuter bike',
  triathlon: 'Triathlon bike',
  folding: 'Folding bike',
  hybrid: 'Hybrid bike',
}

// Thousands separator, so £15,000 reads as it does in the design.
export function formatValue(value) {
  return Number(value).toLocaleString('en-GB')
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

// Out-of-range values clamp to the nearest bound rather than being discarded:
// a link built with ?value=20000 still lands on a usable estimate.
export function clampBikeValue(value) {
  const number = Number.parseInt(value, 10)
  if (!Number.isFinite(number)) {
    return DEFAULT_BIKE_VALUE
  }
  const bounded = clamp(number, BIKE_MIN, BIKE_MAX)
  // The slider only stops on multiples of BIKE_STEP above BIKE_MIN. Snapping
  // here keeps the thumb, the heading and the priced value in agreement — an
  // off-grid value would render one number and price another.
  const snapped =
    BIKE_MIN + Math.round((bounded - BIKE_MIN) / BIKE_STEP) * BIKE_STEP

  return clamp(snapped, BIKE_MIN, BIKE_MAX)
}

export function clampAge(value) {
  const number = Number.parseInt(value, 10)
  if (!Number.isFinite(number)) {
    return DEFAULT_AGE
  }

  return clamp(number, AGE_MIN, AGE_MAX)
}

// Unknown values are dropped, not echoed. Returns '' when there is no match.
export function normaliseBikeType(value) {
  const key = String(value || '')
    .trim()
    .toLowerCase()

  return Object.prototype.hasOwnProperty.call(BIKE_TYPES, key) ? key : ''
}

export function bikeTypeLabel(value) {
  return BIKE_TYPES[normaliseBikeType(value)] || ''
}

// The URL a CTA should point at. Built here so that every link into the funnel
// emits parameters /quote actually accepts — an unknown bike type or a
// malformed src would simply be dropped on arrival, silently losing the
// attribution the link was added for.
//
// This is the no-JavaScript path: it is what a crawler follows, what a
// middle-click opens, and what a screen reader reads out. It must be a real,
// server-rendered href, never a value assigned after hydration.
export function quotePath({ bike = '', src = '' } = {}) {
  const params = []
  const bikeType = normaliseBikeType(bike)
  if (bikeType) {
    params.push(`bike=${bikeType}`)
  }
  const source = sanitiseSrc(src)
  if (source) {
    params.push(`src=${source}`)
  }

  return params.length ? `/quote?${params.join('&')}` : '/quote'
}

// Attribution only. Never rendered, so the constraint here exists to keep
// junk out of the analytics payload rather than out of the DOM.
export function sanitiseSrc(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()

  return /^[a-z0-9\-_/]{1,64}$/.test(raw) ? raw : ''
}

// Everything /quote accepts on the URL, validated in one pass. Personal data
// is deliberately not part of this contract: postcode, date of birth and email
// are collected inside the modal and never travel in a query string.
export function parseEstimatorQuery(query = {}) {
  return {
    bike: normaliseBikeType(query.bike),
    bikeValue: clampBikeValue(query.value),
    age: clampAge(query.age),
    src: sanitiseSrc(query.src),
  }
}

// The site-wide default discount is part of the price: an estimate computed
// without it drops the moment the customer clicks through to a real quote.
export async function fetchDefaultDiscount($axios) {
  try {
    const data = await $axios.$get('/api/policy/v1/discount', {
      timeout: 10000,
    })

    return Number(data && data.discount) || 0
  } catch (error) {
    // A missing discount means a higher price, never a lower one, so falling
    // back to zero cannot understate what we advertise.
    return 0
  }
}

// The annual premium for a slider position. Returns null rather than 0 when the
// engine can't be reached, so callers can keep the last good figure on screen
// instead of flashing a price of nothing.
export async function fetchEstimate($axios, { bikeValue, age, discount = 0 }) {
  try {
    const price = await $axios.$post(
      '/api/policy/v1/price',
      {
        postcode: PLACEHOLDER_POSTCODE,
        discount,
        age: clampAge(age),
        bicycles: [{ make: 'pedalsure', value: clampBikeValue(bikeValue) }],
        insurance_components: ESTIMATE_COMPONENTS,
      },
      { timeout: 10000 }
    )
    const total = Number(price && price.total)

    return Number.isFinite(total) && total > 0 ? total : null
  } catch (error) {
    return null
  }
}

// The cached cheapest premium behind every "from £x a month" claim, or null
// when the engine can't price it — callers then drop the claim rather than
// publish a figure nobody can be quoted.
export async function fetchEntryPrice($axios) {
  try {
    const data = await $axios.$get('/api/policy/v1/entry-price', {
      timeout: 10000,
    })
    // A 204 (engine unreachable) arrives as an empty body.
    return data && data.annual ? data : null
  } catch (error) {
    return null
  }
}

// The figure a page server-renders. Uses the cached entry price when the
// sliders are at their defaults — that is the same number the "from £x a
// month" claim is built on — and prices the engine directly otherwise.
export async function fetchInitialEstimate(
  $axios,
  { bikeValue = DEFAULT_BIKE_VALUE, age = DEFAULT_AGE } = {}
) {
  const entryPrice = await fetchEntryPrice($axios)

  const isDefault = bikeValue === DEFAULT_BIKE_VALUE && age === DEFAULT_AGE
  if (isDefault) {
    return { entryPrice, price: entryPrice ? entryPrice.annual : null }
  }

  const discount = await fetchDefaultDiscount($axios)
  const price = await fetchEstimate($axios, { bikeValue, age, discount })

  return { entryPrice, price }
}
